import { IScope, ITimeoutService, IIntervalService, ILocationService, IPromise, IWindowService } from 'angular';
import { Html5QrcodeSupportedFormats } from "html5-qrcode";
import { PWAService } from './PWAService';
import { QRCodeService } from './QRCodeService';
import TaskInfoService from '../../services/core/TaskInfoService';
import type { ITelemetryBeaconService } from '../../services/core/TelemetryBeaconService';
import { QRAssessmentMetadata } from './models/QRAssessmentMetadata';
import { IAssessmentPackageManagerClient } from '../../serviceworkerBridgeClients/IAssessmentPackageManagerClient';
import { IPreloadBridgeClient } from '../../serviceworkerBridgeClients/IPreloadBridgeClient';
import { IResponseSupportBridgeClient } from '../../serviceworkerBridgeClients/IResponseSupportBridgeClient';
import { ListAssessmentsSortField, AssessmentPackageManagerMessageType, FileExistenceStatus } from '../../../../../serviceworker/src/bridges/assessmentPackageManagerMessages';
import { AssessmentPackageMetadata } from 'serviceworker/src/models/AssessmentPackageMetadata';
import { IQRCodeScanner, IQRCodeScannerFactory } from './IQRCodeScanner';
import { Html5QrcodeScannerState } from './Html5QrcodeFactory';
import { forceDigest } from '../../utils/angularDigest';

export enum AccessBlockedReason {
    NOT_CHROMEOS = 'NOT_CHROMEOS',
    NOT_KIOSK = 'NOT_KIOSK',
    NO_SCHOOL_CODE = 'NO_SCHOOL_CODE',
    INVALID_SCHOOL_CODE = 'INVALID_SCHOOL_CODE',
}

interface PWAScope extends IScope {
    downloadProgress: number;
    isKiosk: boolean;
    isManaged: boolean;
    accessBlocked: boolean;
    accessBlockedReason: AccessBlockedReason | null;
    accessCheckPending: boolean;
}

declare global {
    interface Window {
        launchQueue?: {
            setConsumer(callback: (launchParams: { files: FileSystemFileHandle[] }) => void): void;
        };
    }
}

interface FileSystemFileHandle {
    getFile(): Promise<File>;
    name: string;
}

declare global {
    interface WorkerNavigator {
        mediaDevices: MediaDevices;
    }
}

export class PWAController {
    private qrCodeScanner: IQRCodeScanner | null = null;
    private progressInterval: ReturnType<IIntervalService> | null = null;
    public isDownloading = false;
    public isUnpacking = false;
    public errorMessage: string | null = null;
    public errorMessageValues: any = {};
    public assessmentReady = false;
    public showQrScanner = false;
    public isQrError = false;
    public lastDownloadMetadata: { url: string; metadata: QRAssessmentMetadata } | null = null;
    public storedAssessments: (AssessmentPackageMetadata & { isExpired?: boolean })[] = [];
    public loadingAssessments = false;
    private isFullscreen: boolean = false;
    private isManaged: boolean = false;
    private downloadState = {
        metadata: null as { url: string; metadata: QRAssessmentMetadata } | null,
        status: 'idle' as 'idle' | 'downloading' | 'error' | 'unpacking' | 'ready',
        error: null as string | null,
        progress: 0
    };

    static $inject = [
        '$scope',
        '$location',
        '$timeout',
        '$interval',
        '$window',
        'PWAService',
        'QRCodeService',
        'AssessmentPackageManagerClient',
        'QRCodeScannerFactory',
        'PreloadBridgeClient',
        'TaskInfoService',
        'TelemetryBeaconService',
        'ResponseSupportBridgeClient'
    ];

    /** Status of pending recovery auto-retry: null | 'retrying' | 'success' | 'failed' */
    public pendingRecoveryStatus: string | null = null;
    /** Whether the recovery failure overlay is visible (tapped the warning icon). */
    public showRecoveryOverlay: boolean = false;
    /** Successful recoveries from the current startup auto-retry (shown in a bottom panel). */
    public recoveredResponses: Array<{ targetFilename?: string; candidateInfo?: any; subject?: string }> = [];

    constructor(
        private $scope: PWAScope,
        private $location: ILocationService,
        private $timeout: ITimeoutService,
        private $interval: IIntervalService,
        private $window: IWindowService,
        private PWAService: PWAService,
        private QRCodeService: QRCodeService,
        private AssessmentPackageManagerClient: IAssessmentPackageManagerClient,
        private QRCodeScannerFactory: IQRCodeScannerFactory,
        private PreloadBridgeClient: IPreloadBridgeClient,
        private TaskInfoService: TaskInfoService,
        private TelemetryBeaconService: ITelemetryBeaconService,
        private ResponseSupportBridgeClient: IResponseSupportBridgeClient
    ) {
        this.$scope.downloadProgress = 0;
        this.$scope.isKiosk = false;
        this.$scope.isManaged = false;
        this.$scope.accessBlocked = false;
        this.$scope.accessBlockedReason = null;
        this.$scope.accessCheckPending = !this.isDevMode();

        // Check if we're in fullscreen mode
        this.isFullscreen = this.PWAService.isFullScreen();
        document.addEventListener('fullscreenchange', () => {
            console.log("PWAController: Fullscreen changed");
            this.isFullscreen = this.PWAService.isFullScreen();
            this.updateKioskModeUsingTimeout();
        });

        // Check if we're in managed mode and update kiosk status
        this.PWAService.isManaged().then((isManaged) => {
            this.updateKioskModeUsingTimeout(isManaged);
        });

        // In production, check whether this device meets provisioning requirements
        if (!this.isDevMode()) {
            this.checkDeviceAccess();
        }

        // Before anything that records telemetry, so those events are deliverable.
        this.startTelemetryBeforeLogin();

        this.setupServiceWorkerMessageListener();
        this.setupLaunchQueue();
        this.setupResponseSupportShortcut();
        this.setupDevModeShortcuts();
        this.loadStoredAssessments();
        this.startPreloading();

        // Check for pending recovery uploads and retry automatically (non-blocking).
        this.checkAndRetryPendingRecoveries();

        this.$scope.$on('$destroy', () => {
            if (this.cancelRecoveryRetry) {
                this.cancelRecoveryRetry();
            }
            if (this.recoveryStatusTimer) {
                this.$timeout.cancel(this.recoveryStatusTimer);
            }
        });
    }

    /** True once the pre-login telemetry session has been started for this page. */
    private preLoginTelemetryStarted = false;

    /**
     * record() wrapper that can never throw into the caller.
     *
     * Every call site here is an error path, where a telemetry failure must not
     * replace the real failure being reported.
     */
    private recordTelemetry(
        eventType: string,
        severity: 'info' | 'warning' | 'critical',
        data: Record<string, string | number | boolean>,
    ): void {
        try {
            this.TelemetryBeaconService?.record(eventType, severity, data);
        } catch (error) {
            // Deliberately silent: see above.
        }
    }

    /**
     * Starts telemetry for the pre-login phase of the start screen.
     *
     * Everything on this screen used to be unreportable. record() early-returns into
     * a pending queue until start() has run, and start() was only ever called from
     * tryUnlockSuccess, so qr.scan_success, qr.scan_error, the camera errors and all
     * three recovery.auto_retry_* events were recorded and then discarded unless the
     * same page load also completed a login. That also made it pointless to
     * instrument the failures that stop a candidate reaching login at all.
     *
     * The context is partial by nature: there is no userUuid or candidateNo yet, and
     * a virgin device with no package has no telemetryUrl either, which is the one
     * case that stays unreportable. The login start() later opens its own session;
     * both are tied together by device_id.
     */
    private startTelemetryBeforeLogin(): void {
        try {
            if (!this.TelemetryBeaconService) return;
            // Idempotent: this is retried whenever a package becomes available, and a
            // second start() would reset the buffer and open a new sessionId.
            if (this.preLoginTelemetryStarted) return;

            const telemetryUrl = this.PWAService.getTelemetryUrl();
            if (!telemetryUrl) {
                // No package loaded yet, so no ingest host is known. Called again from
                // startDownload() and openAssessment(), right after saveCurrentMetadata
                // supplies one.
                return;
            }
            this.preLoginTelemetryStarted = true;

            // PWAService is untyped JS, so getCurrentMetadata() widens to Object.
            const metadata = (this.PWAService.getCurrentMetadata() || {}) as Partial<AssessmentPackageMetadata>;
            this.PWAService.resolveTelemetrySchoolCode(metadata).then((resolved: { schoolCode: number | null; source: string }) => {
                this.TelemetryBeaconService.start({
                    telemetryUrl,
                    sessionId: (metadata.assessment_uuid || 'nopackage') + '-prelogin-' + Date.now(),
                    assessmentUuid: metadata.assessment_uuid || '',
                    userUuid: '',
                    deviceId: '',
                    swInstanceId: '',
                    schoolCode: resolved.schoolCode,
                    packageType: metadata.package_type || '',
                });
                this.TelemetryBeaconService.record('session.start', 'info', {
                    phase: 'pre_login',
                    browserUA: navigator.userAgent,
                    navigatorOnline: navigator.onLine,
                    schoolCodeSource: resolved.source,
                    candidateCodeSource: 'not_applicable_pre_login',
                });
            }).catch((error: unknown) => {
                // Released so a later call can retry: the flag is claimed before the
                // async resolve, so leaving it set on failure would block every retry.
                this.preLoginTelemetryStarted = false;
                console.warn('PWAController: Pre-login telemetry start failed', error);
            });
        } catch (error) {
            // Telemetry must never break the start screen.
            this.preLoginTelemetryStarted = false;
            console.warn('PWAController: Pre-login telemetry start threw', error);
        }
    }

    /**
     * Trigger preloading of app shell bundles via the service worker.
     *
     * Fires the manifest-fetch / startup-download via startPreload() first
     * (this is synchronous — the SW does its own async work). Then reads
     * the Google Admin-provisioned school code via PWAService and, if
     * present, sends an activation message so the SW can check the
     * manifest's schoolCodeAllowlist. On unmanaged devices the school
     * code is null and the activation message never fires — the preloader
     * stays in its gated-idle state (or runs unrestricted if the manifest
     * has no allowlist).
     */
    private async startPreloading(): Promise<void> {
        this.PreloadBridgeClient.startPreload();

        const rawSchoolCode = await this.PWAService.getProvisionedSchoolCode();
        if (rawSchoolCode === null) {
            return;
        }

        const schoolCode = parseInt(rawSchoolCode, 10);
        if (Number.isNaN(schoolCode)) return;

        this.PreloadBridgeClient.activateForSchool(schoolCode);
    }

    /**
     * Checks for pending recovery entries and retries them automatically.
     * Runs async and never blocks the start screen.
     */
    /** Handle to cancel the in-flight recovery when the scope is destroyed. */
    private cancelRecoveryRetry: (() => void) | null = null;
    /** Handle to the 8s success indicator clear timeout. */
    private recoveryStatusTimer: angular.IPromise<void> | null = null;

    private async checkAndRetryPendingRecoveries(): Promise<void> {
        let cancelled = false;
        this.cancelRecoveryRetry = () => { cancelled = true; };

        // Collect successful recovery details via listener
        const onSuccess = (_id: number, targetFilename?: string, candidateInfo?: any, subject?: string) => {
            if (cancelled) return;
            this.recoveredResponses.push({ targetFilename, candidateInfo, subject });
            this.forceDigest();
        };
        this.ResponseSupportBridgeClient.addRetrySuccessListener(onSuccess);

        try {
            const count = await this.ResponseSupportBridgeClient.getPendingRecoveryCount();
            if (count === 0) return;

            console.log(`PWAController: Found ${count} pending recovery upload(s), starting auto-retry`);
            this.pendingRecoveryStatus = 'retrying';
            this.forceDigest();

            if (this.TelemetryBeaconService) {
                this.TelemetryBeaconService.record('recovery.auto_retry_started', 'info', { count: String(count) });
            }

            const result = await this.ResponseSupportBridgeClient.retryAllPendingRecoveries();
            if (cancelled) return;

            if (this.TelemetryBeaconService) {
                this.TelemetryBeaconService.record('recovery.auto_retry_completed', 'info', {
                    successCount: String(result.successCount),
                    failCount: String(result.failCount),
                });
            }

            if (result.failCount > 0) {
                // Show failure even if some succeeded — the failure is more critical
                this.pendingRecoveryStatus = 'failed';
                // Do NOT auto-clear the failure indicator — user must see it
            } else if (result.successCount > 0) {
                this.pendingRecoveryStatus = 'success';
                // The success popup panel stays until dismissed; clear just the
                // small indicator icon after a generous delay so it doesn't linger
                // after the user closes the popup.
                this.recoveryStatusTimer = this.$timeout(() => { this.pendingRecoveryStatus = null; }, 30000);
            } else {
                this.pendingRecoveryStatus = null;
            }
            this.forceDigest();
        } catch (e) {
            if (cancelled) return;
            console.error('PWAController: Auto-retry of pending recoveries failed:', e);
            if (this.TelemetryBeaconService) {
                this.TelemetryBeaconService.record('recovery.auto_retry_error', 'critical', {
                    error: (e instanceof Error ? e.message : String(e)).substring(0, 200),
                });
            }
            this.pendingRecoveryStatus = 'failed';
            this.forceDigest();
            // Do NOT auto-clear — user must see persistent failure indicator
        } finally {
            this.ResponseSupportBridgeClient.removeRetrySuccessListener(onSuccess);
            this.cancelRecoveryRetry = null;
        }
    }

    private forceDigest(): void {
        forceDigest(this.$scope);
    }

    public toggleRecoveryOverlay(): void {
        this.showRecoveryOverlay = !this.showRecoveryOverlay;
    }

    /**
     * Check if we're in dev mode
     */
    public isDevMode(): boolean {
        // @ts-ignore - __BUILD_NO__ is defined by webpack DefinePlugin
        return typeof __BUILD_NO__ !== 'undefined' && __BUILD_NO__ === 'dev-local';
    }

    /**
     * Sets up dev-only shortcuts for testing.
     * Ctrl+Shift+T loads the test specimen assessment.
     */
    private setupDevModeShortcuts(): void {
        if (!this.isDevMode()) {
            return;
        }
        console.log('PWAController: Dev mode detected, enabling dev shortcuts (Ctrl+Shift+T to load test specimen)');

        const keydownHandler = (event: KeyboardEvent) => {
            // Ctrl+Shift+T to load test specimen
            if (event.ctrlKey && event.shiftKey && event.key === 'T') {
                event.preventDefault();
                this.loadTestSpecimen();
            }
        };
        document.addEventListener('keydown', keydownHandler);

        // Clean up when scope is destroyed
        this.$scope.$on('$destroy', () => {
            document.removeEventListener('keydown', keydownHandler);
        });
    }

    /**
     * Load the test specimen assessment directly (dev mode only)
     */
    public async loadTestSpecimen(): Promise<void> {
        if (!this.isDevMode()) {
            console.warn('PWAController: loadTestSpecimen is only available in dev mode');
            return;
        }

        console.log('PWAController: Loading test specimen...');
        this.isDownloading = true;
        this.errorMessage = null;
        if (!this.$scope.$$phase) {
            this.$scope.$apply();
        }

        try {
            const response = await fetch('/testdata/testspecimen.ibassessment');
            if (!response.ok) {
                throw new Error(`Failed to fetch test specimen: ${response.status}`);
            }

            const blob = await response.blob();
            const file = new File([blob], 'testspecimen.ibassessment', { type: 'application/octet-stream' });

            console.log('PWAController: Test specimen fetched, sending to service worker...');
            this.isUnpacking = true;
            if (!this.$scope.$$phase) {
                this.$scope.$apply();
            }

            // Use AssessmentPackageManagerClient.feedDownloadedFile directly
            // This is the same method used by the QR code download flow
            const testMetadata = {
                filename: 'testspecimen.ibassessment',
                filesize: file.size,
                timestamp: new Date().toISOString(),
                package_type: 'school',
                valid_from: '2020-01-01',
                valid_to: '2030-12-31',
                env: 'DEV',
                assessment_uuid: 'test-specimen-uuid',
                subject: 'Test Subject',
                year: 2024,
                month: 6,
                title: 'Test Specimen',
                language: 'en',
                system_lang_selectable: true,
                response_upload_api_url: '',
                hybrid_response_api_url: ''
            };
            await this.AssessmentPackageManagerClient.feedDownloadedFile(file, testMetadata);
            console.log('PWAController: Test specimen loaded successfully');

            // Reset loading flags
            this.isDownloading = false;
            this.isUnpacking = false;

            // Refresh the stored assessments list
            await this.loadStoredAssessments();

            // Update the UI
            if (!this.$scope.$$phase) {
                this.$scope.$apply();
            }

        } catch (error) {
            console.error('PWAController: Failed to load test specimen:', error);
            this.errorMessage = 'Failed to load test specimen: ' + (error as Error).message;
            this.isDownloading = false;
            this.isUnpacking = false;
            if (!this.$scope.$$phase) {
                this.$scope.$apply();
            }
        }
    }

    private setupServiceWorkerMessageListener(): void {
        if (!('serviceWorker' in navigator)) {
            console.error('ServiceWorker is not supported');
            return;
        }

        const messageListener = async (event: MessageEvent) => {
            //console.log("Frontend received message from service worker", event);
            if (event.data.type === 'assessmentOpened') {
                console.log("Assessment file open event received from worker!");
                if (this.$scope.accessBlocked) {
                    console.log("PWAController: Ignoring assessmentOpened — device access is blocked");
                    this.isDownloading = false;
                    this.isUnpacking = false;
                    return;
                }
                // If access check is still pending, wait for it to settle before proceeding
                if (this.$scope.accessCheckPending) {
                    console.log("PWAController: Deferring assessmentOpened — access check pending");
                    const unwatch = this.$scope.$watch('accessCheckPending', (pending: boolean) => {
                        if (!pending) {
                            unwatch();
                            if (!this.$scope.accessBlocked) {
                                this.handleAssessmentOpened(messageListener);
                            } else {
                                console.log("PWAController: Deferred assessmentOpened dropped — device blocked after check");
                                this.isDownloading = false;
                                this.isUnpacking = false;
                            }
                        }
                    });
                    return;
                }
                this.handleAssessmentOpened(messageListener);
            }
        };

        if ('serviceWorker' in navigator) {
            (navigator.serviceWorker as ServiceWorkerContainer).addEventListener('message', messageListener);
        }

        this.$scope.$on('$destroy', () => {
            if ('serviceWorker' in navigator) {
                (navigator.serviceWorker as ServiceWorkerContainer).removeEventListener('message', messageListener);
            }
        });
    }

    private handleAssessmentOpened(messageListener: (event: MessageEvent) => void): void {
        this.isDownloading = false;
        this.showQrScanner = false;
        this.isUnpacking = false;
        this.assessmentReady = true;

        this.$timeout(() => {
            document.location.hash = "#/welcome";
        }, 1000);
        if ('serviceWorker' in navigator) {
            (navigator.serviceWorker as ServiceWorkerContainer).removeEventListener('message', messageListener);
        }
    }

    private setupLaunchQueue(): void {
        if ("launchQueue" in window && window.launchQueue) {
            console.log("PWAController: launchQueue is supported");
            window.launchQueue.setConsumer(async (launchParams) => {
                if (launchParams.files && launchParams.files.length) {
                    const file = launchParams.files[0];
                    if (file.name.endsWith('.ibassessment')) {
                        console.log("PWAController: .ibassessment file received via launchQueue");
                        await this.handleIbAssessmentFile(file);
                    } else {
                        console.warn("PWAController: Unsupported file type received via launchQueue");
                    }
                }
            });
        }
    }

    private async handleIbAssessmentFile(fileHandle: FileSystemFileHandle): Promise<void> {
        console.log("PWAController: Handling .ibassessment file");
        this.isDownloading = true;
        this.$scope.downloadProgress = 0;
        this.errorMessage = null;

        try {
            const file = await fileHandle.getFile();

            this.progressInterval = this.$interval(() => {
                this.$scope.downloadProgress = Math.min(this.$scope.downloadProgress + 2, 100);
                if (this.$scope.downloadProgress >= 100) {
                    this.$interval.cancel(this.progressInterval);
                }
            }, 1000);

            await this.PWAService.handleIbAssessmentFile(file);
            console.log("PWAController: File handling completed successfully");
            this.isDownloading = false;
            this.isUnpacking = true;
        } catch (error) {
            console.error("PWAController: File handling failed:", error);
            this.isDownloading = false;
            this.isUnpacking = false;
            this.errorMessage = "Failed to process the assessment file. Please try again.";
            // A package that will not open blocks the candidate entirely, and this
            // path reported nothing.
            this.recordTelemetry('package.unpack_failed', 'critical', {
                source: 'file_handle',
                errorType: error instanceof Error ? (error.name || 'Error') : 'Unknown',
                errorMessage: (error instanceof Error ? error.message : String(error)).substring(0, 200),
            });
        } finally {
            console.log("PWAController: File handling process finished");
            if (this.progressInterval) {
                this.$interval.cancel(this.progressInterval);
            }
            this.$scope.$apply();
        }
    }

    public async startDownload(url: string, metadata: QRAssessmentMetadata): Promise<void> {
        console.log("PWAController: Starting download", { url, metadata });

        this.downloadState.status = 'downloading';
        this.downloadState.error = null;
        this.downloadState.progress = 0;

        if (this.downloadState.status === 'downloading') {
            this.downloadState.metadata = { url, metadata };
        }

        this.isDownloading = true;
        this.isUnpacking = false;
        this.assessmentReady = false;
        this.errorMessage = null;
        this.lastDownloadMetadata = null;

        this.PWAService.saveCurrentMetadata(metadata);

        await this.PWAService.resetServiceWorker();
        this.PWAService.resetDownloadProgress();

        // Deliberately AFTER resetServiceWorker: that reset makes the SW collector
        // end its session and stop, so starting beforehand would leave the page
        // believing telemetry was running while the SW had no context and no upload
        // loop. Still before the download below, so a package.download_failed on a
        // virgin device (where this is the first moment an ingest host is known) is
        // actually reportable.
        this.startTelemetryBeforeLogin();

        try {
            const filename = new URL(url).pathname.split('/').pop();

            const checkResult = await this.AssessmentPackageManagerClient.checkFileExistence(
                filename,
                metadata.env,
                metadata.package_type,
                metadata.assessment_uuid,
                metadata.valid_from,
                metadata.valid_to,
                metadata.school_code
            );

            if (checkResult === FileExistenceStatus.SameVersionFound) {
                console.log("PWAController: Correct assessment already loaded, skipping download");
                this.downloadState.status = 'ready';
                this.isDownloading = false;
                this.isUnpacking = false;
                this.assessmentReady = false;
                this.showQrScanner = false;
                if (!this.$scope.$$phase) {
                    this.$scope.$apply();
                }
                return;
            }

            console.log("PWAController: Starting download progress interval");
            this.progressInterval = this.$interval(() => {
                const newProgress = this.PWAService.getDownloadProgress();
                if (newProgress !== this.$scope.downloadProgress) {
                    console.log("PWAController: Download progress updated to", newProgress);
                    this.downloadState.progress = newProgress;
                }
                this.$scope.downloadProgress = newProgress;
            }, 100);

            await this.PWAService.downloadAndSendToServiceWorker(url, metadata);

            this.$timeout(() => {
                this.loadStoredAssessments();
            }, 300);

            console.log("PWAController: Download completed successfully");
            this.downloadState.status = 'ready';
            this.isDownloading = false;
            this.lastDownloadMetadata = null;

        } catch (error: any) {
            console.error("PWAController: Download failed:", error);

            this.downloadState.status = 'error';
            this.isDownloading = false;
            this.assessmentReady = false;

            const errorMsg = error instanceof Error ? error.message : String(error);

            // Package acquisition is the last step before a candidate can log in, so
            // a failure here is exam-blocking and was previously invisible.
            this.recordTelemetry('package.download_failed', 'critical', {
                packageType: metadata.package_type || '',
                assessmentUuid: metadata.assessment_uuid || '',
                env: metadata.env || '',
                storageInsufficient: errorMsg.startsWith('STORAGE_INSUFFICIENT:'),
                errorType: error instanceof Error ? (error.name || 'Error') : 'Unknown',
                errorMessage: errorMsg.substring(0, 200),
            });

            if (errorMsg.startsWith('STORAGE_INSUFFICIENT:')) {
                const parts = errorMsg.split(':');
                this.errorMessage = 'MODULES.PWA-START.ERROR-STORAGE-LOW';
                this.errorMessageValues = {
                    available_mb: parts[1],
                    required_mb: parts[2]
                };
            } else if (
                (error instanceof DOMException && error.name === 'QuotaExceededError') ||
                errorMsg.includes('QuotaExceededError')
            ) {
                this.errorMessage = 'MODULES.PWA-START.ERROR-STORAGE-FULL';
                this.errorMessageValues = {};
            } else {
                this.downloadState.error = "Failed to download the assessment. Please check your internet connection and try again.";
                this.errorMessage = this.downloadState.error;
            }

            if (this.downloadState.metadata) {
                this.lastDownloadMetadata = this.downloadState.metadata;
            }
        } finally {
            console.log("PWAController: Download process finished");
            if (this.progressInterval) {
                this.$interval.cancel(this.progressInterval);
            }
            if (!this.$scope.$$phase) {
                this.$scope.$apply();
            }
        }
    }

    public retryDownload(): void {
        if (!this.downloadState.metadata || this.downloadState.status !== 'error') {
            console.error("Cannot retry download - invalid state");
            return;
        }

        const { url, metadata } = this.downloadState.metadata;
        this.startDownload(url, metadata);
    }

    public async prepareCamForQrCodeScan(): Promise<void> {
        console.log("PWAController: Starting camera preparation");
        const html5QrcodeData = localStorage.getItem("HTML5_QRCODE_DATA");
        let storedCameraId: string | null = null;

        console.log("PWAController: Checking for stored camera data:", html5QrcodeData);
        if (html5QrcodeData) {
            try {
                const data = JSON.parse(html5QrcodeData);
                storedCameraId = data.lastUsedCameraId;
                console.log("PWAController: Found stored camera ID:", storedCameraId);
            } catch (e) {
                console.warn("PWAController: Invalid HTML5_QRCODE_DATA format, removing it", e);
                localStorage.removeItem("HTML5_QRCODE_DATA");
            }
        }

        try {
            console.log("PWAController: Requesting camera permissions...");
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            console.log("PWAController: Camera permissions granted");

            console.log("PWAController: Stopping initial permission stream");
            stream.getTracks().forEach(track => {
                console.log("PWAController: Stopping track:", track.label);
                track.stop();
            });

            console.log("PWAController: Enumerating devices...");
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            console.log("PWAController: Found video devices:", videoDevices.map(d => ({
                deviceId: d.deviceId,
                label: d.label,
                groupId: d.groupId
            })));

            if (videoDevices.length === 0) {
                console.error("PWAController: No video devices found!");
                throw new Error("No video input devices found");
            }

            console.log(`PWAController: Found ${videoDevices.length} video devices`);

            const storedCameraExists = storedCameraId &&
                videoDevices.some(device => device.deviceId === storedCameraId);
            console.log("PWAController: Stored camera still exists?", storedCameraExists);

            if (!storedCameraExists && html5QrcodeData) {
                console.log("PWAController: Stored camera no longer available, removing HTML5_QRCODE_DATA");
                localStorage.removeItem("HTML5_QRCODE_DATA");
            }

            if (!storedCameraExists) {
                const newCameraId = videoDevices[0].deviceId;
                console.log("PWAController: Setting new default camera:", {
                    deviceId: newCameraId,
                    label: videoDevices[0].label
                });
                localStorage.setItem("HTML5_QRCODE_DATA", JSON.stringify({
                    hasPermission: true,
                    lastUsedCameraId: newCameraId
                }));
            }

        } catch (error) {
            console.error("PWAController: Error during camera preparation:", error);
            console.error("PWAController: Error details:", {
                name: error.name,
                message: error.message,
                stack: error.stack
            });
            localStorage.removeItem("HTML5_QRCODE_DATA");
            throw error;
        }
    }

    public async initializeQrCodeScanner(): Promise<void> {
        const config = {
            fps: 10,
            qrbox: 300,
            formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE]
        };


        await this.prepareCamForQrCodeScan()
            .catch(err => {
                console.error("Permission denied or error occurred:", err);
                try {
                    this.TelemetryBeaconService.record('qr.camera_error', 'warning', {
                        reason: err.name === 'NotFoundError' ? 'not_found'
                              : err.name === 'NotAllowedError' ? 'permission_denied'
                              : 'unknown',
                        errorName: err.name || 'Unknown',
                        errorMessage: (err.message || '').substring(0, 200),
                    });
                } catch (e) {
                    // telemetry must never break error handling
                }
                if (err.name === 'NotFoundError') {
                    this.errorMessage = "MODULES.PWA-START.ERROR-NO-CAMERA";
                    this.errorMessageValues = {
                        error_details: err.message || ''
                    };
                    this.showQrScanner = false;
                } else if (err.name === 'NotAllowedError') {
                    this.errorMessage = "MODULES.PWA-START.ERROR-PERMISSION-DENIED";
                    this.errorMessageValues = {
                        error_details: err.message || ''
                    };
                } else {
                    this.errorMessage = "MODULES.PWA-START.ERROR-GENERIC";
                    this.errorMessageValues = {
                        error_type: err.message || 'Unknown error',
                        error_details: err.message || ''
                    };
                }
                this.$scope.$apply();
            })
            .then(() => {
                console.log('Creating QR code scanner using factory');
                this.qrCodeScanner = this.QRCodeScannerFactory.create(
                    "qr-code-scanner",
                    config,
                    false);
                this.qrCodeScanner.render(
                    this.handleQrCodeScanSuccess.bind(this),
                    this.handleQrCodeScanError.bind(this)
                );
            })

        return Promise.resolve();
    }

    public async handleQrCodeScanSuccess(decodedText: string): Promise<void> {
        console.log(`PWAController: QR Code scanned: ${decodedText}`);

        try {
            this.lastDownloadMetadata = null;

            if (this.qrCodeScanner?.getState() === Html5QrcodeScannerState.SCANNING.toString()) {
                this.qrCodeScanner?.pause(true);
            }

            const { downloadUrl, metadata } = await this.QRCodeService.processQRCode(decodedText);
            console.log('QR code processed successfully:', metadata);

            this.clearError();

            this.stopQrCodeScanner(true);

            await this.startDownload(downloadUrl, metadata);
        } catch (error) {
            console.error('QR code processing failed:', error);

            // Stop the scanner and show error message
            this.stopQrCodeScanner(true);
            this.lastDownloadMetadata = null;

            // Handle different types of errors
            if (error.status === 404) {
                this.errorMessage = 'MODULES.PWA-START.ERROR-404';
                this.errorMessageValues = {
                    error_code: '404',
                    error_details: error.message || ''
                };
            } else if (error.name === 'TypeError' && error.message?.includes('Failed to fetch')) {
                // This typically indicates a CORS or network issue
                this.errorMessage = 'MODULES.PWA-START.ERROR-NETWORK';
                this.errorMessageValues = {
                    error_details: 'Unable to connect to the assessment server. This may be due to network connectivity issues or server configuration problems.'
                };
            } else if (error.message?.includes('Invalid QR code')) {
                this.errorMessage = 'MODULES.PWA-START.ERROR-INVALID-QR';
                this.errorMessageValues = {
                    error_details: error.message || ''
                };
            } else if (error.message?.includes('Invalid host')) {
                this.errorMessage = 'MODULES.PWA-START.ERROR-INVALID-HOST';
                this.errorMessageValues = {
                    error_details: error.message || ''
                };
            } else {
                this.errorMessage = 'MODULES.PWA-START.ERROR-GENERIC';
                this.errorMessageValues = {
                    error_type: error.name || 'Unknown error',
                    error_details: error.message || 'An unexpected error occurred while processing the QR code.'
                };
            }

            this.isQrError = true;

            // Force UI update
            this.$scope.$apply();
        }
    }

    public handleQrCodeScanError(errorMessage: string | any): void {
        if (typeof errorMessage === 'string' &&
            !errorMessage.includes('No MultiFormat Readers were able to detect the code') &&
            !errorMessage.includes('No barcode or QR code detected')) {
            console.error(`PWAController: QR Code scan error: ${errorMessage}`);
            try {
                this.TelemetryBeaconService.record('qr.scanner_error', 'warning', {
                    reason: errorMessage.includes('NotReadableError') ? 'camera_busy'
                          : errorMessage.includes('NotAllowedError') ? 'permission_revoked'
                          : 'runtime_error',
                    message: errorMessage.substring(0, 200),
                });
            } catch (e) {
                // telemetry must never break error handling
            }

            if (errorMessage.includes('NotReadableError')) {
                this.errorMessage = 'MODULES.PWA-START.ERROR-CAMERA-NOT-READABLE';
                this.errorMessageValues = {
                    error_details: errorMessage
                };
                this.isQrError = true;
                if (!this.$scope.$$phase) {
                    this.$scope.$apply();
                }
            } else if (errorMessage.includes('NotAllowedError')) {
                this.errorMessage = 'MODULES.PWA-START.ERROR-CAMERA-NOT-ALLOWED';
                this.errorMessageValues = {
                    error_details: errorMessage
                };
                this.isQrError = true;
                if (!this.$scope.$$phase) {
                    this.$scope.$apply();
                }
            }
        }

        if (this.qrCodeScanner?.getState() === Html5QrcodeScannerState.PAUSED.toString()) {
            this.qrCodeScanner?.resume();
        }
    }

    public clearError(): void {
        this.downloadState.status = 'idle';
        this.downloadState.error = null;
        this.downloadState.metadata = null;
        this.downloadState.progress = 0;

        this.errorMessage = null;
        this.errorMessageValues = {};
        this.isQrError = false;
        this.lastDownloadMetadata = null;

        if (!this.$scope.$$phase) {
            this.$scope.$apply();
        }
    }

    public startQrCodeScanner(): void {
        this.stopQrCodeScanner(true);

        this.clearError();
        this.isDownloading = false;
        this.isUnpacking = false;
        this.assessmentReady = false;
        this.showQrScanner = true;

        if (!this.$scope.$$phase) {
            this.$scope.$apply();
        }

        this.$timeout(() => {
            this.initializeQrCodeScanner();
        });
    }

    public stopQrCodeScanner(destroy: boolean = false): void {
        this.showQrScanner = false;

        if (this.qrCodeScanner) {
            try {
                if (destroy) {
                    if (this.qrCodeScanner.getState() === Html5QrcodeScannerState.SCANNING.toString()) {
                        this.qrCodeScanner.pause(true);
                    }
                    this.qrCodeScanner.clear();
                    this.qrCodeScanner = null;
                } else {
                    if (this.qrCodeScanner.getState() === Html5QrcodeScannerState.SCANNING.toString()) {
                        this.qrCodeScanner.pause(true);
                    }
                }
            } catch (error) {
                console.error('Error stopping QR scanner:', error);
                this.qrCodeScanner = null;
            }
        }
    }

    public $onDestroy(): void {
        this.stopQrCodeScanner(true);
    }

    public async loadStoredAssessments(): Promise<void> {
        this.loadingAssessments = true;
        this.errorMessage = null;
        try {
            // Sort by timestamp descending to show newest first
            const assessments = await this.AssessmentPackageManagerClient.listAssessments([
                { field: 'timestamp', direction: 'desc' }
            ]);

            // Pre-calculate expiration status
            const currentDate = new Date();
            this.storedAssessments = assessments.map(assessment => ({
                ...assessment,
                isExpired: this.calculateExpiration(assessment, currentDate)
            }));

            this.$timeout(() => {
                console.log("PWAController: Stored assessments loaded");
            }, 10);
        } catch (error) {
            console.error('Failed to load stored assessments:', error);
            this.errorMessage = 'Failed to load stored assessments. Please try again.';
        } finally {
            this.loadingAssessments = false;
            this.$scope.$apply();
        }
    }

    private calculateExpiration(metadata: AssessmentPackageMetadata, currentDate: Date): boolean {
        const startDate = new Date(metadata.valid_from);
        const expirationDate = new Date(metadata.valid_to);

        // For school assessments, allow opening for up to 14 days after expiration
        if (metadata.package_type === 'school') {
            const gracePeriodEnd = new Date(expirationDate);
            gracePeriodEnd.setDate(gracePeriodEnd.getDate() + 14);
            return currentDate < startDate || currentDate > gracePeriodEnd;
        }

        return currentDate < startDate || currentDate > expirationDate;
    }

    public async openAssessment(assessmentId: number | string): Promise<void> {
        const assessmentIdNumber = typeof assessmentId === 'string' ? parseInt(assessmentId, 10) : assessmentId;

        try {
            // Find the assessment metadata and save it in PWAService
            const assessment = this.storedAssessments.find(a => a.id === assessmentIdNumber);
            if (assessment) {
                this.PWAService.saveCurrentMetadata(assessment);
                // No-op when the $onInit attempt already succeeded.
                this.startTelemetryBeforeLogin();
            }

            this.TaskInfoService.clear();
            await this.AssessmentPackageManagerClient.loadAssessment(assessmentIdNumber);
            this.assessmentReady = true;
            this.$timeout(() => {
                document.location.hash = "#/welcome";
            }, 1000);
        } catch (error) {
            console.error('Failed to open assessment:', error);
            this.errorMessage = 'Failed to open the assessment. Please try again.';
            this.$scope.$apply();
        }
    }

    private async checkDeviceAccess(): Promise<void> {
        try {
            const status = await this.PWAService.getDeviceAccessStatus();
            if (!status.allowed) {
                this.$scope.accessBlocked = true;
                this.$scope.accessBlockedReason = status.reason as AccessBlockedReason;
                this.$scope.isKiosk = false;
                console.log('PWAController: Device access blocked, reason:', status.reason);
                // A device that cannot start an exam previously reported nothing at
                // all. Fires at most once per visit to the start screen.
                this.recordTelemetry('access.blocked', 'critical', {
                    reason: String(status.reason || 'unknown'),
                    isChromeOS: this.PWAService.isChromeOS(),
                    displayModeFullscreen: window.matchMedia('(display-mode: fullscreen)').matches,
                });
            }
        } catch (error) {
            console.error('PWAController: Failed to determine device access status:', error);
            this.$scope.accessBlocked = true;
            this.$scope.accessBlockedReason = AccessBlockedReason.NOT_KIOSK;
            this.$scope.isKiosk = false;
            this.recordTelemetry('access.blocked', 'critical', {
                reason: 'check_threw',
                errorMessage: (error instanceof Error ? error.message : String(error)).substring(0, 200),
            });
        } finally {
            this.$scope.accessCheckPending = false;
            if (!this.$scope.$$phase) {
                this.$scope.$apply();
            }
        }
    }

    public dismissAccessBlock(): void {
        if (!this.$scope.accessBlocked) {
            return;
        }
        const confirmed = this.$window.confirm(
            'This mode is for testing purposes only and must not be used for actual examinations. Do you wish to continue?'
        );
        if (confirmed) {
            this.$scope.accessBlocked = false;
            this.$scope.accessBlockedReason = null;
            console.log('PWAController: Access block dismissed by user, using school code 0');
            // Activate preloading with school code 0 for the bypass session
            this.PreloadBridgeClient.activateForSchool(0);
        }
    }

    private updateKioskModeUsingTimeout(isManaged?: boolean) {
        this.$timeout(() => {
            this.updateKioskMode(isManaged);
        }, 1);
    }

    public openResponseSupport(): void {
        this.$location.path('/response-support');
    }

    /**
     * Sets up keyboard shortcut for response support tool.
     * Ctrl+Shift+S or Ctrl+Alt+R navigates to the response support tool.
     */
    private setupResponseSupportShortcut(): void {
        const keydownHandler = (event: KeyboardEvent) => {
            // Ctrl+Shift+S or Ctrl+Alt+R to open response support tool
            if ((event.ctrlKey && event.shiftKey && event.key === 'S') ||
                (event.ctrlKey && event.altKey && event.key === 'R')) {
                event.preventDefault();
                if (this.$scope.accessBlocked || this.$scope.accessCheckPending) {
                    return;
                }
                this.openResponseSupport();
                if (!this.$scope.$$phase) {
                    this.$scope.$apply();
                }
            }
        };
        document.addEventListener('keydown', keydownHandler);

        // Clean up when scope is destroyed
        this.$scope.$on('$destroy', () => {
            document.removeEventListener('keydown', keydownHandler);
        });
    }

    async updateKioskMode(isManaged?: boolean) {
        // Update isManaged state and log the change
        const wasManaged = this.isManaged;
        this.isManaged = isManaged !== undefined ? isManaged : await this.PWAService.isManaged();
        if (wasManaged !== this.isManaged) {
            console.log('PWAController: Updating isManaged state from:', wasManaged, 'to:', this.isManaged);
        }

        // Log current state
        console.log('PWAController: Kiosk Check - Current state - isManaged:', this.isManaged, 'isFullscreen:', this.isFullscreen);

        // Determine if kiosk mode should be active
        // During the initial check, we allow kiosk mode if:
        // 1. We're in managed mode AND
        // 2. We're in fullscreen AND
        // 3. wasOutsideFullscreen is either null or 'false'
        // After the initial check, we only allow kiosk mode if:
        // 1. We're in managed mode AND
        // 2. We're in effective fullscreen
        const shouldBeKiosk = this.isManaged && this.isFullscreen && !this.$scope.accessBlocked;

        // Update kiosk mode if needed
        if (shouldBeKiosk !== this.$scope.isKiosk) {
            this.$scope.isKiosk = shouldBeKiosk;
            if (this.$scope.isKiosk) {
                this.PWAService.activateWakeLock();
                console.log('PWAController: Kiosk mode active, wake lock activated');
            } else {
                console.log('PWAController: Kiosk mode not active, wake lock not activated');
            }
        }
    }
}


export default function registerPWAController(controllers: angular.IModule): void {
    controllers.controller('PWAController', PWAController);
}
